import { createReducer } from '@reduxjs/toolkit'

import { MAX_PAGE_SIZE, Pagination, PaginationQuery } from '@/constants/pagination'
import { axios } from '@/utils/axios'

import { createAppAsyncThunk } from './utils'

const namespace = 'salesSettings'

export enum SalesAttribution {
  MESSAGE_SENDER = 1,
  LAST_CHATTER = 2,
  EMPLOYEE_ON_SHIFT = 3,
}

enum SalesSettingsStatus {
  Inactivated = 0,
  activated = 1,
}

export interface SalesSettings {
  id: number
  createdAt: string
  effectAt: string
  excludeAutoMessages: boolean
  excludeFanRefunds: boolean
  excludeOnlyFansServiceFee: boolean
  excludeWelcomeMessages: boolean
  messageStatus: boolean
  messageType: SalesAttribution
  postsStatus: boolean
  postsType: SalesAttribution
  referralsStatus: boolean
  referralsType: SalesAttribution
  saleSetHours: number
  saleSetMinutes: number
  status: SalesSettingsStatus
  streamsStatus: boolean
  streamsType: SalesAttribution
  subscriptionStatus: boolean
  subscriptionType: SalesAttribution
  tipsMessagesStatus: boolean
  tipsPostsStatus: boolean
  tipsProfileStatus: boolean
  tipsStoriesStatus: boolean
  tipsStreamsStatus: boolean
  tipsType: SalesAttribution
  workingOnSameShift: boolean
}

export const readSalesSettingsHistory = createAppAsyncThunk(
  `${namespace}/readSalesSettingsHistory`,
  async ({
    pageNum = 1,
    pageSize = MAX_PAGE_SIZE,
  }: PaginationQuery = {}) => {
    const { data } = await axios.get<Pagination<SalesSettings>>('/sale-setting/history', {
      params: {
        pageNum,
        pageSize,
      },
    })
    return data
  },
)

export const readActivatedSalesSettings = createAppAsyncThunk(
  `${namespace}/readCurrentSalesSettings`,
  async () => {
    const { data } = await axios.get<SalesSettings>('/sale-setting/get-current-setting')
    return data
  },
)

export type CreateSalesSettingParams = Omit<SalesSettings, 'id' | 'createdAt' | 'effectAt'>

export const createSalesSettings = createAppAsyncThunk(
  `${namespace}/createSalesSetting`,
  async (params: CreateSalesSettingParams) => {
    const { data } = await axios.put<SalesSettings>('/sale-setting/edit', params)
    return data
  },
)

const initialState: {
  history: SalesSettings[]
  activatedSalesSettings?: SalesSettings
} = {
  history: [],
  activatedSalesSettings: undefined,
}

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(readSalesSettingsHistory.fulfilled, (draft, { payload }) => {
    draft.history = payload.rows
  })
  builder.addCase(readActivatedSalesSettings.fulfilled, (draft, { payload }) => {
    draft.activatedSalesSettings = payload
  })
  builder.addCase(createSalesSettings.fulfilled, (draft, { payload }) => {
    draft.activatedSalesSettings = payload
  })
})

export default { [namespace]: reducer }
